import React, { useRef } from 'react';
import OptimizedImage from './OptimizedImage';

/**
 * A horizontally scrolling row of photos.
 *
 * Replaces a `repeat(auto-fit, minmax(...))` grid, which reflows to a
 * different number of columns at every width and strands an orphan photo on
 * the last row whenever the count doesn't divide evenly. A strip is always one
 * row, whatever the count, so 9 photos and 6 photos look equally deliberate.
 *
 * Scroll-snap does the work: on a phone it's a native swipe with no JS, and
 * the arrows only appear where there's a pointer to click them with. Every
 * tile is forced to the same aspect ratio so the row has a straight baseline
 * even though the photos don't all share one shape.
 */
const PhotoStrip = ({ photos, ratio = '3 / 2', tile = 340 }) => {
  const scroller = useRef(null);

  const nudge = (direction) => {
    const el = scroller.current;
    if (!el) return;
    // One tile plus its gap, so a click always lands cleanly on the next photo.
    el.scrollBy({ left: direction * (tile + 16), behavior: 'smooth' });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={scroller}
        className="photo-strip"
        style={{
          display: 'flex',
          gap: '1rem',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          // Full-bleed edges on a phone so the row reads as scrollable rather
          // than as a grid that happens to be cut off.
          padding: '0.25rem 0 1rem',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {photos.map(([slug, alt]) => (
          <div
            key={slug}
            style={{
              flex: `0 0 min(${tile}px, 85%)`,
              scrollSnapAlign: 'center',
            }}
          >
            <OptimizedImage
              slug={slug}
              alt={alt}
              sizes={`min(${tile}px, 85vw)`}
              rounded
              style={{ aspectRatio: ratio }}
            />
          </div>
        ))}
      </div>

      {[['‹', -1, 'Previous photos'], ['›', 1, 'Next photos']].map(([glyph, dir, label]) => (
        <button
          key={label}
          type="button"
          aria-label={label}
          onClick={() => nudge(dir)}
          className="photo-strip-arrow"
          style={{
            position: 'absolute',
            top: 'calc(50% - 1.5rem)',
            [dir === -1 ? 'left' : 'right']: '-0.5rem',
            transform: 'translateY(-50%)',
            width: '2.75rem',
            height: '2.75rem',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(255,255,255,0.94)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            color: 'var(--charcoal)',
            fontSize: '1.6rem',
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          {glyph}
        </button>
      ))}

      <style>{`
        .photo-strip { scrollbar-width: none; }
        .photo-strip::-webkit-scrollbar { display: none; }
        /* Arrows are pointless on touch — swiping is the gesture, and they'd
           just cover part of a photo. */
        @media (hover: none), (max-width: 700px) {
          .photo-strip-arrow { display: none; }
        }
      `}</style>
    </div>
  );
};

export default PhotoStrip;
